import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, X, RefreshCw, RotateCcw, TerminalSquare, Shield, GraduationCap, ArrowUpRight } from 'lucide-react';
import { generateAIResponse, testAISystem, endChatSession, type ChatSource } from '../utils/aiResponses';
import { renderMarkdown } from '../utils/markdownParser';
import { detectFirewall, getRandomFirewallMessages } from '../utils/firewallDetection';
import { supabase } from '../lib/supabase';

const DEFAULT_WELCOME = "Hey. I'm Joe — the AI version. Ask me what I've built, how I built it, or what broke along the way.";
const DEFAULT_PROMPTS = [
  "What's the one you'd build again?",
  "Tell me about a hard part.",
  "What's the hardest thing you've shipped?",
];

function pickRandom<T>(items: T[]): T {
  return items[Math.floor(Math.random() * items.length)];
}

function stripTrailingCitations(text: string, sources?: ChatSource[]): string {
  if (!text) return text;
  const sourceTitles = new Set(
    (sources ?? []).map((s) => s.title.trim()).filter(Boolean)
  );
  const lines = text.split('\n');
  let end = lines.length;
  while (end > 0) {
    const line = lines[end - 1].trim();
    if (line === '') {
      end--;
      continue;
    }
    const citationMatch = line.match(/^[—–-]\s*(.+)$/);
    if (!citationMatch) break;
    const cited = citationMatch[1].trim();
    if (sourceTitles.size > 0 && !sourceTitles.has(cited)) break;
    end--;
  }
  return lines.slice(0, end).join('\n').replace(/\s+$/, '');
}

interface Message {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
  sources?: ChatSource[];
}

interface ChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenTerminal?: () => void;
  /** Optional pull-and-clear function for a one-shot prefilled user message. */
  consumeInitialMessage?: () => string | null;
}

type ConnectionStatus = 'connecting' | 'online' | 'offline';

const SCAFFOLD_PATTERNS = /scaffold|build\.hiimjoe\.com|context engineering (education|course|program|platform)|six-layer context stack/i;

function mentionsScaffold(content: string): boolean {
  return SCAFFOLD_PATTERNS.test(content);
}

const ScaffoldCTA: React.FC = () => (
  <motion.a
    href="https://build.hiimjoe.com"
    target="_blank"
    rel="noopener noreferrer"
    initial={{ opacity: 0, y: 6 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.15 }}
    className="mt-s3 flex items-center gap-s3 p-s3 rounded-sm bg-accent/10 border border-accent/25 hover:border-accent/40 hover:bg-accent/15 transition-colors cursor-pointer group"
  >
    <div className="p-s2 rounded-xs bg-accent/20 flex-shrink-0">
      <GraduationCap size={18} className="text-accent" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-text leading-tight">Explore Scaffold</p>
      <p className="font-mono text-[10px] uppercase tracking-[0.8px] text-text-dim mt-1">AI Context Engineering</p>
    </div>
    <ArrowUpRight size={16} className="text-accent flex-shrink-0 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
  </motion.a>
);

const ChatModal: React.FC<ChatModalProps> = ({ isOpen, onClose, onOpenTerminal, consumeInitialMessage }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [status, setStatus] = useState<ConnectionStatus>('connecting');
  const [firewallDetected, setFirewallDetected] = useState(false);
  const [firewallMessages, setFirewallMessages] = useState<[string, string] | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>(DEFAULT_PROMPTS);
  const picks = useMemo(
    () => [...suggestions].sort(() => Math.random() - 0.5).slice(0, 3),
    [suggestions, isOpen]
  );
  const [welcomePool, setWelcomePool] = useState<string[]>([DEFAULT_WELCOME]);
  const welcomeAddedRef = useRef(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const connectionTestedRef = useRef(false);

  const handleClose = useCallback(() => {
    void endChatSession();
    onClose();
  }, [onClose]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const checkConnection = useCallback(async () => {
    setStatus('connecting');
    setFirewallDetected(false);
    setFirewallMessages(null);
    const startTime = Date.now();

    try {
      const isReady = await testAISystem();
      const elapsed = Date.now() - startTime;
      const minDisplayTime = 1500;

      if (elapsed < minDisplayTime) {
        await new Promise(resolve => setTimeout(resolve, minDisplayTime - elapsed));
      }

      if (isReady) {
        setStatus('online');
      } else {
        setStatus('offline');
        // Connection test failed — check if a firewall is the cause
        const detection = await detectFirewall();
        if (detection.isFirewallLikely) {
          setFirewallDetected(true);
          setFirewallMessages(getRandomFirewallMessages());
        }
      }
    } catch {
      setStatus('offline');
      // Also check for firewall on exception
      try {
        const detection = await detectFirewall();
        if (detection.isFirewallLikely) {
          setFirewallDetected(true);
          setFirewallMessages(getRandomFirewallMessages());
        }
      } catch {
        // Firewall detection itself failed — stay with generic offline
      }
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    if (!connectionTestedRef.current) {
      connectionTestedRef.current = true;
      checkConnection();
    }

    if (!welcomeAddedRef.current) {
      welcomeAddedRef.current = true;
      (async () => {
        let pool: string[] = [DEFAULT_WELCOME];
        try {
          const { data, error } = await supabase
            .from('ai_prompts')
            .select('variables')
            .eq('prompt_name', 'joeai_system_prompt_v1')
            .eq('active', true)
            .maybeSingle();

          if (!error && data?.variables) {
            const vars = data.variables as {
              welcome_messages?: unknown;
              suggested_prompts?: unknown;
            };
            const welcomes = Array.isArray(vars.welcome_messages)
              ? (vars.welcome_messages as unknown[]).filter(
                  (m): m is string => typeof m === 'string' && m.trim().length > 0
                )
              : [];
            const prompts = Array.isArray(vars.suggested_prompts)
              ? (vars.suggested_prompts as unknown[]).filter(
                  (p): p is string => typeof p === 'string' && p.trim().length > 0
                )
              : [];
            if (welcomes.length > 0) {
              pool = welcomes;
              setWelcomePool(welcomes);
            }
            if (prompts.length > 0) {
              setSuggestions(prompts);
            }
          }
        } catch {
          // Fall through to defaults — do not touch network-blocked flow
        }
        setTimeout(() => {
          addMessage(pickRandom(pool), false);
        }, 500);
      })();
    }

    setTimeout(() => inputRef.current?.focus(), 600);
  }, [isOpen, checkConnection]);

  // Pull in any one-shot prefilled message (e.g. from an article CTA).
  useEffect(() => {
    if (!isOpen || !consumeInitialMessage) return;
    const prefill = consumeInitialMessage();
    if (prefill) {
      setInput(prefill);
      setTimeout(() => inputRef.current?.focus(), 600);
    }
  }, [isOpen, consumeInitialMessage]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);

  const addMessage = (
    content: string,
    isUser: boolean,
    sources?: ChatSource[]
  ) => {
    const newMessage: Message = {
      id: Date.now().toString() + Math.random(),
      content,
      isUser,
      timestamp: new Date(),
      sources,
    };
    setMessages(prev => [...prev, newMessage]);
  };

  const handleSendMessage = async () => {
    if (!input.trim() || isTyping) return;

    const userMessage = input.trim();
    setInput('');
    addMessage(userMessage, true);

    if (status === 'offline') {
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        addMessage(
          "AI system is currently offline. Please contact Joe directly at j.hamilton@servetdigital.com",
          false
        );
      }, 1000);
      return;
    }

    setIsTyping(true);
    try {
      const response = await generateAIResponse(userMessage, messages);

      // generateAIResponse catches errors internally and may return a fallback.
      // If it flagged a network error, treat it as offline and run firewall detection.
      if (response.isNetworkError) {
        setIsTyping(false);
        setStatus('offline');
        try {
          const detection = await detectFirewall();
          if (detection.isFirewallLikely) {
            setFirewallDetected(true);
            setFirewallMessages(getRandomFirewallMessages());
          }
        } catch {
          // Firewall detection itself failed — stay with generic offline
        }
        return;
      }

      setStatus('online');
      setTimeout(() => {
        setIsTyping(false);
        const cleaned = stripTrailingCitations(response.message, response.sources);
        addMessage(cleaned, false, response.sources);
      }, 800);
    } catch (error) {
      console.error('Chat error:', error);
      setIsTyping(false);
      setStatus('offline');
      // Exception escaped generateAIResponse — also check for firewall
      try {
        const detection = await detectFirewall();
        if (detection.isFirewallLikely) {
          setFirewallDetected(true);
          setFirewallMessages(getRandomFirewallMessages());
          return;
        }
      } catch {
        // Firewall detection itself failed
      }
      addMessage(
        "I'm experiencing technical difficulties. Please try again or contact Joe directly.",
        false
      );
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleReset = () => {
    setMessages([]);
    setInput('');
    setIsTyping(false);
    setTimeout(() => {
      addMessage(pickRandom(welcomePool), false);
    }, 300);
    inputRef.current?.focus();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.98, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: 12 }}
            transition={{ type: 'spring', duration: 0.5 }}
            className="fixed inset-4 md:inset-auto md:right-6 md:bottom-6 md:top-6 md:w-[500px] z-50 flex flex-col bg-bg-elev border border-border-hi rounded-sm shadow-glass overflow-hidden"
          >
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between px-s5 py-s3 border-b border-border flex-shrink-0">
                <div className="flex items-center gap-s3 min-w-0">
                  <h2 className="font-mono text-[12px] uppercase tracking-[1.2px] text-text truncate">
                    Joe<span className="text-accent">AI</span>
                  </h2>
                  <div className="flex items-center gap-1.5">
                    <span
                      className={`w-2 h-2 rounded-full ${
                        status === 'connecting'
                          ? 'bg-warn animate-pulse'
                          : status === 'online'
                            ? 'bg-ok'
                            : 'bg-err'
                      }`}
                    />
                    <span className="font-mono text-[10px] uppercase tracking-[1px] text-text-muted">
                      {status === 'connecting'
                        ? 'Connecting'
                        : status === 'online'
                          ? 'Online'
                          : 'Offline'}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-s1">
                  <button
                    onClick={handleReset}
                    title="Start over"
                    aria-label="Reset conversation"
                    className="p-s2 rounded-xs text-text-dim hover:text-text transition-colors"
                  >
                    <RotateCcw size={14} />
                  </button>

                  <button
                    onClick={handleClose}
                    aria-label="Close"
                    className="p-s2 rounded-xs text-text-dim hover:text-text transition-colors"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-s5 py-s5 space-y-s4">
                <AnimatePresence>
                  {messages.map((message) => (
                    <motion.div
                      key={message.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex ${message.isUser ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className="max-w-[85%]">
                        <div
                          className={`rounded-sm px-s4 py-s3 ${
                            message.isUser
                              ? 'bg-accent/15 border border-accent/30 text-text'
                              : 'bg-surface border border-border text-text'
                          }`}
                        >
                          <div className="leading-[1.55] text-[14px]">
                            {message.isUser ? message.content : renderMarkdown(message.content)}
                          </div>
                          <div className="font-mono text-[10px] uppercase tracking-[0.8px] text-text-dim mt-s2">
                            {message.timestamp.toLocaleTimeString()}
                          </div>
                        </div>
                        {!message.isUser && mentionsScaffold(message.content) && (
                          <ScaffoldCTA />
                        )}
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>

                {isTyping && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex justify-start"
                  >
                    <div className="bg-surface border border-border rounded-sm px-s4 py-s3">
                      <div className="flex items-center gap-s2">
                        <div className="flex space-x-1">
                          {[0, 1, 2].map((i) => (
                            <motion.div
                              key={i}
                              className="w-1.5 h-1.5 bg-accent rounded-full"
                              animate={{ y: [0, -6, 0] }}
                              transition={{
                                duration: 0.6,
                                repeat: Infinity,
                                delay: i * 0.1,
                              }}
                            />
                          ))}
                        </div>
                        <span className="font-mono text-[10px] uppercase tracking-[0.8px] text-text-muted">
                          Thinking
                        </span>
                      </div>
                    </div>
                  </motion.div>
                )}

                <div ref={messagesEndRef} />
              </div>

              <div className="border-t border-border px-s5 py-s4 shrink-0">
                {messages.length === 1 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="mb-s3 flex flex-wrap gap-s2"
                  >
                    {picks.map((suggestion, i) => (
                      <button
                        key={i}
                        onClick={() => setInput(suggestion)}
                        className="inline-flex items-center px-s3 py-1 rounded-full font-mono text-[11px] bg-surface text-text-muted border border-border hover:text-text hover:border-border-hi transition-colors"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </motion.div>
                )}

                <div className="flex gap-s2 items-end">
                  <div className="flex-1 bg-bg-sunken border border-border rounded-xs focus-within:border-border-hi transition-colors">
                    <textarea
                      ref={inputRef}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Ask me anything about Joe's work…"
                      className="w-full bg-transparent border-none outline-none text-text placeholder-text-dim text-[14px] leading-[1.5] resize-none px-s3 py-s2"
                      rows={1}
                      style={{ minHeight: '44px', maxHeight: '120px' }}
                      disabled={isTyping || status === 'offline'}
                    />
                  </div>

                  <motion.button
                    onClick={handleSendMessage}
                    disabled={!input.trim() || isTyping || status === 'offline'}
                    aria-label="Send"
                    className="h-11 w-11 flex items-center justify-center rounded-xs bg-accent text-white hover:bg-accent-glow disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    whileTap={{ scale: 0.96 }}
                  >
                    <Send size={14} />
                  </motion.button>
                </div>

                {status === 'offline' && !firewallDetected && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="mt-s3 p-s3 bg-err/10 border border-err/40 rounded-sm flex items-center justify-between"
                  >
                    <span className="font-mono text-[11px] uppercase tracking-[0.6px] text-err">
                      AI system offline — try again later
                    </span>
                    <button
                      onClick={() => {
                        connectionTestedRef.current = false;
                        checkConnection();
                      }}
                      className="flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.6px] text-err hover:text-text transition-colors"
                    >
                      <RefreshCw size={12} />
                      <span>Retry</span>
                    </button>
                  </motion.div>
                )}

                {status === 'offline' && firewallDetected && firewallMessages && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="mt-s3 p-s4 bg-warn/10 border border-warn/40 rounded-sm space-y-s3"
                  >
                    <div className="flex items-start gap-s2">
                      <Shield size={14} className="text-warn mt-0.5 shrink-0" />
                      <p className="text-[13px] text-text leading-[1.55]">{firewallMessages[0]}</p>
                    </div>
                    <p className="text-[13px] text-text-muted leading-[1.55] pl-[22px]">
                      {firewallMessages[1]}
                    </p>

                    <div className="flex items-center gap-s3 pl-[22px]">
                      <button
                        onClick={() => {
                          handleClose();
                          onOpenTerminal?.();
                        }}
                        className="inline-flex items-center gap-1.5 px-s3 py-1 rounded-xs bg-accent/15 border border-accent/40 text-accent hover:bg-accent/25 hover:border-accent/60 font-mono text-[11px] uppercase tracking-[0.6px] transition-colors"
                      >
                        <TerminalSquare size={12} />
                        <span>Open Terminal</span>
                      </button>
                      <button
                        onClick={() => {
                          connectionTestedRef.current = false;
                          checkConnection();
                        }}
                        className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.6px] text-text-muted hover:text-text transition-colors"
                      >
                        <RefreshCw size={12} />
                        <span>Retry</span>
                      </button>
                    </div>

                    <p className="font-mono text-[10px] uppercase tracking-[0.8px] text-text-dim pl-[22px]">
                      Tip: try from a personal device off your corporate network.
                    </p>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ChatModal;
